import { ColDef, ColGroupDef } from '@ag-grid-community/core';
import getHours from 'date-fns/getHours';
import parse from 'date-fns/parse';
import formatDate from 'date-fns/format';

import {
  formatColumnDate,
  dateFilterParams,
  getValueBgClass,
  formatLongPrice,
  getPureNumber,
  headerInfoTooltipDef,
  transformPercentageValue,
  formatPercentage,
} from '../lib/utils';
import styled from '@benzinga/themetron';

const defaultFilterParams = {
  newRowsAction: 'keep',
};

const BillionsFilter = [
  {
    label: '$0 to $100M',
    rules: (value: number) => {
      return value >= 0 && value <= 100000000;
    },
  },
  {
    label: '$101M to $500M',
    rules: (value: number) => {
      return value >= 101000000 && value <= 500000000;
    },
  },
  {
    label: '$501M to $1B',
    rules: (value: number) => {
      return value >= 5010000000 && value <= 1000000000;
    },
  },
  {
    label: 'Greater than 1B',
    rules: (value: number) => {
      return value >= 1000000000;
    },
  },
  {
    label: '$0 to -$100M',
    rules: (value: number) => {
      return value <= 0 && value >= -100000000;
    },
  },
  {
    label: '-$101M to -$500M',
    rules: (value: number) => {
      return value <= -101000000 && value >= -500000000;
    },
  },
  {
    label: '-$501M to -$1B',
    rules: (value: number) => {
      return value <= -5010000000 && value >= -1000000000;
    },
  },
  {
    label: 'Less than -$1B',
    rules: (value: number) => {
      return value <= -1000000000;
    },
  },
];

export default [
  {
    cellClass: 'ticker-cell',
    field: 'date',
    filter: 'agDateColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      ...dateFilterParams,
      buttons: ['clear'],
      inRangeInclusive: true,
    },
    headerName: 'Date',
    initialWidth: 80,
    sort: 'desc',
    sortIndex: 0,
    valueFormatter: ({ data }) => formatColumnDate(data.date),
    valueGetter: ({ data }) => {
      if (!data.date) return null;

      return data.date;
    },
  },
  {
    cellClass: 'ticker-cell',
    field: 'time',
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      values: [
        {
          label: 'AM',
          rules: (value: number) => {
            return getHours(value) < 13;
          },
        },
        {
          label: 'PM',
          rules: (value: number) => {
            return getHours(value) >= 13;
          },
        },
      ],
    },
    initialWidth: 70,
    sort: 'desc',
    sortIndex: 1,
    valueFormatter: ({ value }) => {
      if (!value) return '';

      return formatDate(value, 'h:mm a');
    },
    valueGetter: ({ data }) => {
      if (!data.time) return '';

      return parse(data.time, 'HH:mm:ss', new Date());
    },
  },
  {
    cellClass: 'ticker-cell country-cell',
    cellRenderer: ({ data }: { data: any }) => {
      return (
        <>
          <StyledCountryFlagIcon className="country-flag-icon" code={data.country} />
          {data.country}
        </>
      );
    },
    field: 'country',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    initialWidth: 80,
    valueFormatter: ({ data }) => {
      return data.country;
    },
  },
  {
    cellClass: 'ticker-cell',
    field: 'event_name',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    headerName: 'Econ Event',
    initialWidth: 200,
  },
  {
    cellClass: 'ticker-cell',
    field: 'event_period',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    headerName: 'Period',
    initialWidth: 70,
    valueGetter: ({ data }) => {
      if (!data.event_period && !data.period_year) return '-';

      return `${data.event_period} ${data.period_year}`;
    },
  },
  {
    cellClass: ({ value }) => getValueBgClass(value),
    cellStyle: { fontWeight: 'bold', textAlign: 'left' },
    field: 'prior',
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
      values: BillionsFilter,
    },
    initialWidth: 100,
    valueFormatter: ({ value }) => formatLongPrice(value, ''),
    valueGetter: ({ data }) => getPureNumber(data.prior),
  },
  {
    cellClass: 'ticker-cell',
    field: 'consensus',
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
      values: BillionsFilter,
    },
    headerName: 'Forecast',
    initialWidth: 100,
    valueFormatter: ({ value }) => formatLongPrice(value, ''),
    valueGetter: ({ data }) => getPureNumber(data.consensus),
  },
  {
    cellClass: 'ticker-cell',
    cellStyle: { fontWeight: '900' },
    field: 'actual',
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
      values: BillionsFilter,
    },
    initialWidth: 100,
    valueFormatter: ({ value }) => formatLongPrice(value, ''),
    valueGetter: ({ data }) => getPureNumber(data.actual),
  },
  {
    cellClass: ({ value }) => getValueBgClass(value),
    cellStyle: { fontWeight: 'bold', textAlign: 'left' },
    field: 'surprise',
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
      values: [
        {
          label: '0% - 5%',
          rules: (value: number) => {
            return value >= 0 && value <= 5;
          },
        },
        {
          label: '6% - 10%',
          rules: (value: number) => {
            return value >= 6 && value <= 10;
          },
        },
        {
          label: '11% - 15%',
          rules: (value: number) => {
            return value >= 11 && value <= 15;
          },
        },
        {
          label: '16% - 20%',
          rules: (value: number) => {
            return value >= 16 && value <= 20;
          },
        },
        {
          label: 'Greater than 20%',
          rules: (value: number) => {
            return value >= 20;
          },
        },
      ],
    },
    headerName: 'Surprise',
    initialWidth: 100,
    valueFormatter: ({ value }) => formatPercentage(value),
    valueGetter: ({ data }) => {
      if (data.consensus === '' || data.consensus === undefined || data.actual === '' || data.actual === undefined) {
        return null;
      }

      const value = (data.consensus - data.actual) / data.consensus;

      return transformPercentageValue(value);
    },
  },
  {
    cellClass: 'importance-cell',
    field: 'importance',
    headerName: 'Imp.',
    ...headerInfoTooltipDef('Importance'),
    cellRenderer: ({ data }: { data: any }) => {
      const importanceLevelPercentage = 100 / (5 / Number(data.importance)) || 0;

      return (
        <div className="flex items-center justify-center w-[80px]">
          <StyledImpIndicator
            className="importance-indicator flex items-center w-full bg-blue-100 rounded h-5"
            level={importanceLevelPercentage}
            role="presentation"
          >
            <div
              className={`importance-indicator-value bg-blue-600 h-[18px] rounded text-white flex items-end justify-end pr-1`}
            >
              <span className="ml-2 inline-block">{data.importance}</span>
            </div>
          </StyledImpIndicator>
        </div>
      );
    },
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    initialWidth: 94,
  },

  {
    cellClass: 'ticker-cell',
    cellRenderer: () => {
      return (
        // eslint-disable-next-line react/jsx-no-target-blank
        <a href="https://www.benzinga.com/profile/portfolio/?action=login&utm_source=calendars" target="_blank">
          Get Alert
        </a>
      );
    },
    field: 'get_alert',
    headerName: 'Get Alert',
    initialWidth: 70,
    suppressAutoSize: true,
    suppressMenu: true,
    suppressSizeToFit: true,
  },
] as (ColDef | ColGroupDef)[];

const StyledImpIndicator = styled.span<{ level: number }>`
  border: 1px solid ${({ theme }) => theme.colorPalette.blue200};
  .importance-indicator-value {
    background: linear-gradient(270deg, #1a79ff 0%, #97c0f9 100%);
    width: ${({ level }) => `${level}%`};
  }
`;

const StyledCountryFlagIcon = styled.div<{ code: string }>`
  &.country-flag-icon {
    margin-right: 0.3125rem;
    background-color: #fff;
    background: ${({ code }) =>
        `url(https://www.benzinga.com/wp-content/themes/benzinga-money/dist/rebuild/images/icons/countries-flags/alpha3/${code}.svg)`}
      no-repeat;
    background-size: cover;
    border: 1px solid #d6d6d6;
    display: inline-block;
    vertical-align: middle;
    width: 17px;
    height: 12px;
  }
`;
